import { submitSafely, useAdminAi, type AdminAiAction } from '@church/data'
import {
  AI_ACTION_MAX_CREDITS,
  aiActionSchema,
  creditsLabel,
  type AiActionFormInput,
  type AiActionInput,
} from '@church/domain'
import { faFloppyDisk } from '@church/ui-icons'
import { zodResolver } from '@hookform/resolvers/zod'
import { createFileRoute } from '@tanstack/react-router'
import { useId, useState } from 'react'
import { useForm } from 'react-hook-form'
import {
  Button,
  Card,
  Chip,
  ErrorNote,
  Field,
  Input,
  ListTitle,
  PageHeader,
  Select,
  SidePanel,
  Spinner,
  TableScroll,
  Textarea,
  listRow,
} from '../components/ui.tsx'

/**
 * AI credits in the platform console (docs/15-MUSIC-AND-AI.md, "AI
 * credits"): what each AI action costs a church, in credits — a row each,
 * changed in a panel (credits, on or off, its description; audited) — and
 * the last 30 days across every church: how often each ran, the credits it
 * charged, and what those runs really cost, so a credit can be priced. A
 * plan's monthly credits are set on Plans.
 */
export const Route = createFileRoute('/_authed/admin/ai')({
  component: AdminAi,
})

function AdminAi() {
  const ai = useAdminAi()
  const [openKey, setOpenKey] = useState<string | null>(null)
  if (ai.overview.isPending) return <Spinner />
  const data = ai.overview.data
  if (!data) return <ErrorNote error={ai.overview.error} />
  const open = ai.actions.find((a) => a.key === openKey) ?? null
  const usage = ai.usage

  return (
    <div className="space-y-6">
      <PageHeader visible title="AI" />
      {!data.enabled && (
        <p className="rounded-lg bg-warning-soft px-3 py-2 text-sm text-warning-content">
          This server has no OpenRouter key (OPENROUTER_API_KEY), so no church sees AI and nothing
          is charged.
        </p>
      )}
      <Card bleed>
        <ListTitle title="What each action costs" />
        <ul className="divide-y divide-border-subtle">
          {ai.actions.map((action) => (
            <li key={action.key}>
              <button
                type="button"
                onClick={() => setOpenKey(action.key)}
                className={`${listRow} flex w-full items-center gap-3 text-left hover:bg-surface-sunken`}
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-base text-content-default">
                    {action.name}
                  </span>
                  <span className="block truncate text-sm text-content-muted">
                    {action.description || action.key}
                  </span>
                </span>
                {!action.enabled && (
                  <Chip size="sm" tone="warning">
                    Off
                  </Chip>
                )}
                <Chip size="md" tone="brand">
                  <span className="tabular-nums">{creditsLabel(action.credits)}</span>
                </Chip>
              </button>
            </li>
          ))}
        </ul>
      </Card>
      <p className="text-sm text-content-muted">
        Each plan’s monthly credits are on Plans; a church’s own extra credits are on its page.
      </p>

      {usage && (
        <Card bleed>
          <ListTitle title="Last 30 days" />
          <TableScroll>
            <table className="w-full min-w-xl text-sm">
              <thead className="bg-surface-heading text-left text-xs tracking-wide text-content-muted uppercase">
                <tr>
                  <th className="px-4 py-2 font-medium">Action</th>
                  <th className="px-4 py-2 text-right font-medium">Runs</th>
                  <th className="px-4 py-2 text-right font-medium">Credits</th>
                  <th className="px-4 py-2 text-right font-medium">Real cost</th>
                  <th className="px-4 py-2 text-right font-medium">Per credit</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-subtle">
                {usage.rows.map((row) => (
                  <tr key={row.key}>
                    <td className="px-4 py-3 text-content-default">{row.name}</td>
                    <td className="px-4 py-3 text-right text-content-muted tabular-nums">
                      {row.runs.toLocaleString()}
                      {row.refunded > 0 && (
                        <span className="block text-xs">{row.refunded} refunded</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-content-muted tabular-nums">
                      {row.credits.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right text-content-muted tabular-nums">
                      {ai.dollars(row.costMicros)}
                    </td>
                    <td className="px-4 py-3 text-right text-content-default tabular-nums">
                      {ai.dollars(row.microsPerCredit)}
                    </td>
                  </tr>
                ))}
                {/* Song themes are free to churches (docs/15): what they cost ForHim. */}
                <tr>
                  <td className="px-4 py-3 text-content-default">
                    Song themes
                    <span className="block text-xs text-content-muted">Free: ForHim pays</span>
                  </td>
                  <td className="px-4 py-3 text-right text-content-muted tabular-nums">
                    {usage.freeThemes.calls.toLocaleString()}
                    <span className="block text-xs">calls</span>
                  </td>
                  <td className="px-4 py-3 text-right text-content-muted tabular-nums">—</td>
                  <td className="px-4 py-3 text-right text-content-muted tabular-nums">
                    {ai.dollars(usage.freeThemes.costMicros)}
                  </td>
                  <td />
                </tr>
                {usage.untiedCostMicros > 0 && (
                  <tr>
                    <td className="px-4 py-3 text-content-muted">Calls tied to no charge</td>
                    <td />
                    <td />
                    <td className="px-4 py-3 text-right text-content-muted tabular-nums">
                      {ai.dollars(usage.untiedCostMicros)}
                    </td>
                    <td />
                  </tr>
                )}
                <tr className="font-medium">
                  <td className="px-4 py-3 text-content-default">All actions</td>
                  <td className="px-4 py-3 text-right text-content-default tabular-nums">
                    {usage.totals.runs.toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-right text-content-default tabular-nums">
                    {usage.totals.credits.toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-right text-content-default tabular-nums">
                    {ai.dollars(usage.totals.costMicros)}
                  </td>
                  <td className="px-4 py-3 text-right text-content-default tabular-nums">
                    {ai.dollars(usage.totals.microsPerCredit)}
                  </td>
                </tr>
              </tbody>
            </table>
          </TableScroll>
        </Card>
      )}
      <p className="text-sm text-content-muted">
        Real cost is what OpenRouter charged for every call of those runs, refunded runs included;
        credits are what churches were charged, refunds given back. Song themes cost churches
        nothing, so they are left out of what a credit costs.
      </p>
      {open && <ActionPanel key={open.key} action={open} onClose={() => setOpenKey(null)} />}
    </div>
  )
}

/** An action's panel: its credits, on or off, its description. Save top right. */
function ActionPanel({ action, onClose }: { action: AdminAiAction; onClose: () => void }) {
  const { update } = useAdminAi()
  const formId = useId()
  const form = useForm<AiActionFormInput, unknown, AiActionInput>({
    resolver: zodResolver(aiActionSchema),
    defaultValues: {
      credits: action.credits,
      enabled: action.enabled,
      description: action.description,
    },
  })
  const errors = form.formState.errors
  const submit = form.handleSubmit(async (input) => {
    await update.mutateAsync({ key: action.key, ...input })
    onClose()
  })
  return (
    <SidePanel
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
      title={action.name}
      description={action.key}
      primary={
        <Button
          type="submit"
          form={formId}
          icon={faFloppyDisk}
          className="py-1.5"
          pending={update.isPending}
          pendingLabel="Saving…"
          disabled={!form.formState.isDirty}
        >
          Save
        </Button>
      }
    >
      <form id={formId} onSubmit={submitSafely(submit)} className="space-y-4" noValidate>
        <ErrorNote error={update.error} />
        <Field
          label="Credits"
          description={`What one run costs a church: 0 is free, up to ${AI_ACTION_MAX_CREDITS.toLocaleString()}. Applies to the next run, in every church.`}
          error={errors.credits?.message}
        >
          <Input type="number" inputMode="numeric" min={0} step={1} {...form.register('credits')} />
        </Field>
        <Field
          label="Runs"
          description="Off: churches are refused it, and their screens say so."
          error={errors.enabled?.message}
        >
          <Select
            {...form.register('enabled', {
              setValueAs: (value) => value === true || value === 'true',
            })}
          >
            <option value="true">On</option>
            <option value="false">Off</option>
          </Select>
        </Field>
        <Field
          label="Description"
          description="Shown to churches on their Billing page."
          error={errors.description?.message}
        >
          <Textarea rows={3} maxLength={300} {...form.register('description')} />
        </Field>
      </form>
    </SidePanel>
  )
}
